/**
 * React hook for registering keyboard shortcuts declaratively.
 * Uses ref pattern to avoid re-registration when handlers change.
 */

import { useContext, useEffect, useRef } from 'react';
import { useLatestRef } from '@gonfalon/react/useLatestRef';

import type { ShortcutDefinition } from '../types';

import { KeyboardShortcutsContext } from './keyboard-shortcuts-provider';

/**
 * Configuration for useKeyboardShortcut hook.
 */
export interface UseKeyboardShortcutConfig {
  /** Single shortcut string or array of shortcuts */
  shortcut: string | string[];
  /** Function to execute when shortcut is triggered */
  handler: () => void;
  /** Human-readable label for the shortcut */
  label: string;
  /** Whether the shortcut is enabled. Default: true */
  enabled?: boolean;
  /** Scope for the shortcut. Default: 'global' */
  scope?: string;
  /** Category for grouping shortcuts in UI */
  category?: string;
}

/**
 * Hook for registering keyboard shortcuts in React components.
 * Handles registration lifecycle and prevents unnecessary re-renders.
 *
 * @param config - Configuration object for the keyboard shortcut
 *
 * @example
 * ```tsx
 * // Simple usage
 * useKeyboardShortcut({
 *   shortcut: 'meta+k',
 *   handler: () => openCommandPalette(),
 *   label: 'Open command palette',
 * });
 *
 * // Multiple shortcuts for same action
 * useKeyboardShortcut({
 *   shortcut: ['meta+/', '?'],
 *   handler: () => showHelp(),
 *   label: 'Show help',
 * });
 *
 * // With all options
 * useKeyboardShortcut({
 *   shortcut: 'escape',
 *   handler: handleClose,
 *   label: 'Close modal',
 *   enabled: isModalOpen,
 *   scope: 'modal',
 *   category: 'Modal Actions',
 * });
 * ```
 */
export function useKeyboardShortcut(config: UseKeyboardShortcutConfig): void {
  const shortcuts = useContext(KeyboardShortcutsContext);
  const { shortcut, handler, label, enabled = true, scope = 'global', category } = config;

  // Store handler in ref to keep it stable across renders
  const handlerRef = useLatestRef(handler);

  // Generate stable IDs for registrations
  const idRef = useRef<string[]>([]);

  useEffect(() => {
    if (!shortcuts) {
      throw new Error('useKeyboardShortcut must be used within a KeyboardShortcutsProvider');
    }

    // Normalize shortcut to array
    const shortcutsList = Array.isArray(shortcut) ? shortcut : [shortcut];

    // Generate IDs if needed
    if (idRef.current.length !== shortcutsList.length) {
      idRef.current = shortcutsList.map(
        (_, index) => `use-keyboard-shortcut-${Math.random().toString(36).slice(2)}-${index}`,
      );
    }

    // Create stable handler that calls the ref
    const stableHandler = () => {
      handlerRef.current();
    };

    // Register shortcuts
    const definitions: ShortcutDefinition[] = shortcutsList.map((key, index) => ({
      id: idRef.current[index],
      hotkey: key,
      enabled,
      handler: stableHandler,
      scope,
      label,
      category,
    }));

    shortcuts.register(definitions, scope);

    // Cleanup function
    return () => {
      // Unregister only our specific shortcuts by ID
      for (const id of idRef.current) {
        shortcuts.unregisterById(id);
      }
    };
  }, [shortcuts, shortcut, scope, enabled, label, category]);
}
